import { Injectable, Injector, effect, inject, untracked } from '@angular/core'
import { Title } from '@angular/platform-browser'
import { TitleStrategy, type RouterStateSnapshot } from '@angular/router'
import { LinguiService } from '@tocdk/lingui-angular'
import { BrandService } from './brand.service'

/** The standard title — what every page showed before FR-050, and still does (AC-5.5). */
export const STANDARD_TITLE = 'Tivedo — The growth desk'

/**
 * FR-050 U6 (AC-5.2) — "<page> · <brand display name>" in white-label mode.
 *
 * The page part is the route's `title` (a ROUTE_TITLES msgid), translated with
 * Lingui. Composition:
 * - branded:   "<page> · <display name>", or just the display name;
 * - unbranded: "<page>" or empty — no Tivedo name while the lookup failed (AC-9.5);
 * - standard (incl. the whole admin area, AC-3.6): exactly today's
 *   "Tivedo — The growth desk" on every route, whatever its title (AC-5.5,
 *   driver decision 2026-09-30).
 *
 * Re-runs when the brand changes (lookup, login, support enter/exit, entering or
 * leaving /admin) and when the UI locale changes. Never injects Router (Router
 * depends on TitleStrategy); it remembers the last page msgid instead.
 */
@Injectable({ providedIn: 'root' })
export class BrandTitleStrategy extends TitleStrategy {
  private readonly title = inject(Title)
  private readonly injector = inject(Injector)
  /** The current route's untranslated title (a msgid), if any. */
  private page: string | undefined
  private deps: { brand: BrandService; lingui: LinguiService } | null = null

  /**
   * BrandService (→ AuthService → Router) is resolved on the first title
   * update, not in the constructor: Router itself injects TitleStrategy, so an
   * eager inject would be a DI cycle.
   */
  private get resolved(): { brand: BrandService; lingui: LinguiService } {
    if (!this.deps) {
      const deps = { brand: this.injector.get(BrandService), lingui: this.injector.get(LinguiService) }
      this.deps = deps
      effect(
        () => {
          deps.lingui.locale()
          deps.brand.displayMode()
          deps.brand.displayBrand()
          untracked(() => this.apply())
        },
        { injector: this.injector }
      )
    }
    return this.deps
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    this.page = this.buildTitle(snapshot)?.trim() || undefined
    this.apply()
  }

  private apply(): void {
    const { brand, lingui } = this.resolved
    const mode = brand.displayMode()
    if (mode === 'standard') {
      this.title.setTitle(STANDARD_TITLE)
      return
    }
    const page = this.page ? lingui.t(this.page) : undefined
    if (mode === 'branded') {
      const name = brand.displayBrand()?.displayName
      if (name) {
        this.title.setTitle(page ? `${page} · ${name}` : name)
        return
      }
    }
    // unbranded (or a brand without a name): never a Tivedo name
    this.title.setTitle(page ?? '')
  }
}
